"use client";

import { useEffect } from "react";

const CHUNK_ERROR_RE = /Loading chunk \d+ failed|error loading dynamically imported module/i;
const RELOAD_KEY = "petci_chunk_reload";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Unhandled error:", error);

    // Auto-recover from stale-chunk errors (e.g. after a deploy invalidated old chunks).
    // Reload once, guarded by sessionStorage to prevent loops.
    const isChunkError =
      error.name === "ChunkLoadError" || CHUNK_ERROR_RE.test(error.message);
    if (isChunkError && typeof window !== "undefined") {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
    }
    // Clear the guard on successful render so future chunk errors can self-heal.
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(RELOAD_KEY);
    }
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-6">
      <div className="max-w-md text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-semantic-error/10 flex items-center justify-center">
          <span className="text-semantic-error text-2xl">!</span>
        </div>
        <h1 className="text-xl font-bold text-neutral-900">Something went wrong</h1>
        <p className="text-sm text-neutral-500">{error.message}</p>
        <button
          onClick={reset}
          className="btn-primary"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
